import { useState, useRef } from "react";
import { debounce } from "lodash";
import { useForm } from "react-hook-form";
import useGet from "../../hooks/read/useGet";
import usePut from "../../hooks/usePut";
import toast from "react-hot-toast";
import BreadCrumbs from "../../components/ui/BreadCrumbs";
import PrimaryLoader from "../../components/common/PrimaryLoader";
import Pagination from "../../components/pagination";
import RoundedContainer from "../../components/containers/RoundedContainer";
import { Button } from "../../components/ui";
import SearchBar from "../../components/inputs/SearchBar";
import Select from "../../components/ui/Select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from "../../components/table/Table";
import RequestDetailModal from "../../components/modals/RequestDetailModal";
import ApproveRejectModal from "../../components/modals/ApproveRejectModal";
import Moment from "react-moment";

const breadcrumbs = [
  { label: "Attendance", link: "/attendance" },
  { label: "Regularization Requests", link: "" },
];

const filterOptions = [
  { label: "Pending", value: "pending" },
  { label: "Approved", value: "approved" },
  { label: "Rejected", value: "rejected" },
];

const RegularizationRequests = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [isApproveRejectModalOpen, setIsApproveRejectModalOpen] = useState(false);
  const [action, setAction] = useState(null);

  // Form state for reject reason
  const { control: approveRejectControl, handleSubmit: handleApproveRejectSubmit, reset: resetApproveRejectForm, watch: watchApproveReject } = useForm({
    mode: "onSubmit",
    defaultValues: {
      reject_reason: "",
    },
  });

  const rejectReason = watchApproveReject("reject_reason");

  const initialState = {
    page_no: 0,
    limit: 10,
    search_query: "",
    status: "",
  };

  const {
    list: requestsList,
    paramsObject,
    setGetListParams,
  } = useGet({
    url: "attendance-regularization/list-requests",
    initialData: initialState,
    onError: (err) => {
      toast.error(err.response?.data?.message || "Failed to fetch regularization requests");
    },
  });

  const approveRejectRequest = usePut({
    url: "attendance-regularization/approve-reject",
    refreshUrl: "attendance-regularization/list-requests",
    onSuccess: (response) => {
      if (action === "approve") {
        toast.success("Request approved successfully");
      } else if (action === "reject") {
        toast.success("Request rejected successfully");
      }
      setIsApproveRejectModalOpen(false);
      setSelectedRequest(null);
      setAction(null);
      resetApproveRejectForm();
      setGetListParams({
        ...paramsObject,
        page_no: 0,
      });
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.message ||
          `Failed to ${action} request`
      );
    },
  });

  const debouncedSearch = useRef(
    debounce((value) => {
      setGetListParams({
        ...paramsObject,
        search_query: value,
        page_no: 0,
      });
    }, 300)
  ).current;

  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    debouncedSearch(value);
  };

  const { control, setValue } = useForm({
    mode: "onChange",
    defaultValues: {
      status: "",
    },
  });

  // Handle status filter change directly from Select onChange
  const handleStatusChange = (selectedValue) => {
    // Update the form value
    setValue("status", selectedValue);
    // Update the API params
    setGetListParams({
      ...paramsObject,
      status: selectedValue,
      page_no: 0,
    });
  };

  const handlePageChange = (pageObj) => {
    setGetListParams({
      ...paramsObject,
      page_no: pageObj.selected,
    });
  };

  const handleViewDetails = (request) => {
    setSelectedRequest(request);
    setIsDetailModalOpen(true);
  };

  const handleApprove = (request) => {
    setSelectedRequest(request);
    setAction("approve");
    setIsApproveRejectModalOpen(true);
  };

  const handleReject = (request) => {
    setSelectedRequest(request);
    setAction("reject");
    setIsApproveRejectModalOpen(true);
  };

  const handleConfirmApproveReject = (data) => {
    const payload = {
      attendance_regularization_id: selectedRequest.attendance_regularization_id,
      action: action,
      reject_reason: action === "reject" ? data.reject_reason : "",
    };
    approveRejectRequest.handleAuth.mutate(payload);
  };

  const handleCloseApproveRejectModal = () => {
    resetApproveRejectForm();
    setIsApproveRejectModalOpen(false);
    setSelectedRequest(null);
    setAction(null);
  };

  const getEmployeeName = (request) => {
    return (
      request?.employee?.name ||
      (request?.employee?.first_name
        ? `${request.employee.first_name} ${request.employee.last_name || ""}`.trim()
        : null) ||
      request?.user?.name ||
      (request?.user?.first_name
        ? `${request.user.first_name} ${request.user.last_name || ""}`.trim()
        : null) ||
      "--"
    );
  };

  const formatAttendanceDate = (date, month, year) => {
    return `${date}/${month}/${year}`;
  };

  const getAttendanceDateForMoment = (date, month, year) => {
    return new Date(`${year}-${month}-${date}`);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "--";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "pending":
        return "bg-yellow-100 text-yellow-800";
      case "approved":
        return "bg-green-100 text-green-800";
      case "rejected":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const isLoading = requestsList?.isLoading;
  const data = requestsList?.data?.data || [];
  const totalCount = requestsList?.data?.total_count || 0;

  return (
    <div className="px-4 py-4 mb-20">
      <BreadCrumbs crumbs={breadcrumbs} />

      <RoundedContainer className="flex space-x-4 items-end mb-4">
        <div className="flex-1 max-w-md">
          <SearchBar
            placeholder="Search by employee name..."
            value={searchQuery}
            onChange={handleSearch}
          />
        </div>
        <div className="w-48">
          <Select
            control={control}
            name="status"
            label="Status"
            options={filterOptions}
            onChange={(e) => handleStatusChange(e.target.value)}
          />
        </div>
      </RoundedContainer>

      {isLoading ? (
        <div className="flex justify-center items-center min-h-96">
          <PrimaryLoader />
        </div>
      ) : data.length === 0 ? (
        <RoundedContainer className="text-center py-12">
          <p className="text-gray-500 text-lg">No regularization requests found</p>
        </RoundedContainer>
      ) : (
        <>
          <div className="max-w-full overflow-x-auto">
            <RoundedContainer>
              <Table>
                <TableHead>
                  <TableHeaderCell className="w-1/9 min-w-[140px]">
                    Employee Name
                  </TableHeaderCell>
                  <TableHeaderCell className="w-1/9 min-w-[120px]">
                    Attendance Date
                  </TableHeaderCell>
                  <TableHeaderCell className="w-1/9 min-w-[100px]">
                    Day
                  </TableHeaderCell>
                  <TableHeaderCell className="w-1/9 min-w-[140px]">
                    Request Date
                  </TableHeaderCell>
                  <TableHeaderCell className="w-1/9 min-w-[130px]">
                    Current Status
                  </TableHeaderCell>
                  <TableHeaderCell className="w-1/9 min-w-[130px]">
                    Requested Status
                  </TableHeaderCell>
                  <TableHeaderCell className="w-1/9 min-w-[120px]">
                    Status
                  </TableHeaderCell>
                  <TableHeaderCell className="w-1/9 min-w-[150px]">
                    Actions
                  </TableHeaderCell>
                </TableHead>
                <TableBody>
                  {data.map((request, idx) => (
                    <TableRow key={idx}>
                      <TableCell className="text-dark font-medium text-sm">
                        {getEmployeeName(request)}
                      </TableCell>
                      <TableCell className="text-dark font-medium text-sm">
                        {formatAttendanceDate(
                          request.attendance_date,
                          request.attendance_month,
                          request.attendance_year
                        )}
                      </TableCell>
                      <TableCell className="text-dark font-medium text-sm">
                        <Moment 
                          date={getAttendanceDateForMoment(
                            request.attendance_date,
                            request.attendance_month,
                            request.attendance_year
                          )} 
                          format="dddd" 
                        />
                      </TableCell>
                      <TableCell className="text-dark font-medium text-sm">
                        {formatDate(request.created_at)}
                      </TableCell>
                      <TableCell className="text-dark font-medium text-sm capitalize">
                        {request.current_status?.replace(/_/g, " ")}
                      </TableCell>
                      <TableCell className="text-dark font-medium text-sm capitalize">
                        {request.requested_status?.replace(/_/g, " ")}
                      </TableCell>
                      <TableCell className="text-dark font-medium text-sm">
                        <span
                          className={`${getStatusBadge(
                            request.status
                          )} px-3 py-1 rounded-full text-xs font-medium capitalize`}
                        >
                          {request.status}
                        </span>
                      </TableCell>
                      <TableCell className="text-dark font-medium text-sm">
                        <div className="flex items-center space-x-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleViewDetails(request)}
                          >
                            View
                          </Button>
                          {request.status === "pending" && (
                            <>
                              <Button
                                size="sm"
                                variant="success"
                                onClick={() => handleApprove(request)}
                              >
                                Approve
                              </Button>
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={() => handleReject(request)}
                              >
                                Reject
                              </Button>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </RoundedContainer>
          </div>

          <RoundedContainer className="mt-4">
            <Pagination
              currentPage={paramsObject.page_no}
              lengthofItems={totalCount}
              limit={paramsObject.limit}
              onPageChange={handlePageChange}
            />
          </RoundedContainer>
        </>
      )}

      <RequestDetailModal
        isOpen={isDetailModalOpen}
        onClose={() => {
          setIsDetailModalOpen(false);
          setSelectedRequest(null);
        }}
        request={selectedRequest}
        onApprove={() => {
          setIsDetailModalOpen(false);
          handleApprove(selectedRequest);
        }}
        onReject={() => {
          setIsDetailModalOpen(false);
          handleReject(selectedRequest);
        }}
      />

      <ApproveRejectModal
        isOpen={isApproveRejectModalOpen}
        onClose={handleCloseApproveRejectModal}
        action={action}
        isLoading={approveRejectRequest.isLoading}
        employeeName={getEmployeeName(selectedRequest)}
        attendanceDate={selectedRequest?.attendance_date}
        attendanceMonth={selectedRequest?.attendance_month}
        attendanceYear={selectedRequest?.attendance_year}
        // Form props
        control={approveRejectControl}
        handleSubmit={handleApproveRejectSubmit}
        onConfirm={handleConfirmApproveReject}
        rejectReason={rejectReason}
      />
    </div>
  );
};

export default RegularizationRequests;